import {
  Component,
  OnInit,
  OnDestroy,
  ViewContainerRef,
  inject,
  viewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { environment } from "environments/environment";
import { NgxPermissionsService } from "ngx-permissions";
import { Subscription, Subject } from "rxjs";
import { distinctUntilChanged } from "rxjs/operators";
import { fadeInAnimation } from "./shared/animation/fade-in.animation";
import { User } from "./user/user.model";
import { UserService } from "./user/user.service";
import { noop } from "./shared/noop.function";
import { setSentryUser } from "./shared/error-handler/sentry-user";
import { toastKey } from "./notify/toast-key.const";
import { VersionAlertComponent } from "./ui/version-alert/version-alert.component";
import { VersionCheckService } from "./shared/version/version-check.service";
import { ReturnUrlService } from "./security/authorization/return-url.service";
import { Bind } from "primeng/bind";
import { Toast } from "primeng/toast";
import { MsalLoginComponent } from "./security/msal-login/msal-login.component";
import { HeaderComponent } from "./ui/header/header.component";
import { UserProgressComponent } from "./ui/user-progress/user-progress.component";
import { NotificationHighlightComponent } from "./ui/notification-highlight/notification-highlight.component";
import { FooterComponent } from "./ui/footer/footer.component";

declare const ga: any;

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  animations: [fadeInAnimation],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    VersionAlertComponent,
    Bind,
    Toast,
    MsalLoginComponent,
    HeaderComponent,
    UserProgressComponent,
    NotificationHighlightComponent,
    RouterOutlet,
    FooterComponent,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  router = inject(Router);
  viewContainerRef = inject(ViewContainerRef);
  private userService = inject(UserService);
  private permissionService = inject(NgxPermissionsService);
  private versionCheckService = inject(VersionCheckService);
  private returnUrlService = inject(ReturnUrlService);

  public user: User;
  public toastKey = toastKey;
  public componentUserChange = new Subject<User>();
  public readonly outlet = viewChild<RouterOutlet>("outlet");
  public subscription = new Subscription();

  constructor() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd && environment.production) {
        ga("set", "page", event.urlAfterRedirects);
        ga("send", "pageview");
      }
    });

    this.subscription.add(this.returnUrlService.captureSignedOutDeepLinks());
  }

  ngOnInit() {
    this.subscription.add(
      this.userService.serviceUserChange
        .pipe(distinctUntilChanged())
        .subscribe({
          next: (userId: number) => this.getUser(userId),
          error: noop,
        }),
    );

    this.subscription.add(
      this.componentUserChange.subscribe((user) => {
        this.user = user;
      }),
    );

    this.versionCheckService.start();

    this.getUser();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.versionCheckService.stop();
  }

  getUser(userId?: number): void {
    if (userId === 0) {
      this.componentUserChange.next(null);

      return;
    }

    this.userService.getUser().then(
      (user) => {
        if (user) {
          this.componentUserChange.next(user);
          this.permissionService.loadPermissions(user.getRoles());
          setSentryUser(user);
        }
      },
      (err) => {
        console.error(err);
        this.componentUserChange.next(null);
      },
    );
  }

  getState() {
    const outlet = this.outlet();
    return outlet && outlet.isActivated
      ? outlet.activatedRouteData.state
      : null;
  }
}
